import React from 'react'
import { Check, Crown, Zap, Users, ArrowRight, MessageSquare, BarChart, Clock } from 'lucide-react'

export const PricingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-white/20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="https://cfcpcmswpbygvdbyrivt.supabase.co/storage/v1/object/sign/images/syncsimplelogo-color.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9hMThjNGE0NC1hYzkzLTQwYTYtYTk5Yy0wY2NlYTdiZDZmM2UiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvc3luY3NpbXBsZWxvZ28tY29sb3IucG5nIiwiaWF0IjoxNzUxMjE4MjA0LCJleHAiOjE5MDg4OTgyMDR9.qXFyzRLEKJUYFtL5qHQCoCkPHsxjIwQzSWdhHw1Zfms"
                alt="SyncSimple Logo"
                className="h-8 w-auto"
              />
            </div>
            <nav className="flex items-center space-x-6">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</a>
              <a href="https://app.syncsimple.ai" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                Get Started
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Async Standups for
            <span className="text-indigo-600"> Modern Teams</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Run efficient async standups directly in Slack. Get AI-powered insights, track team progress, 
            and keep everyone aligned without the meeting fatigue.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="/auth/login" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors flex items-center space-x-2"
            >
              <span>Start 7-Day Free Trial</span>
              <ArrowRight className="h-5 w-5" />
            </a>
            <div className="text-sm text-gray-500">
              No credit card required
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Everything you need for async standups
            </h2>
            <p className="text-xl text-gray-600">
              Streamline your team communication with powerful features built for remote teams
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-white/80 shadow-sm">
              <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Slack Integration</h3>
              <p className="text-gray-600">
                Submit standups directly in Slack with our /standup command. Voice messages supported.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-white/80 shadow-sm">
              <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <BarChart className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Insights</h3>
              <p className="text-gray-600">
                Get daily team summaries, sentiment analysis, and actionable insights powered by AI.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-white/80 shadow-sm">
              <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Clock className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Time Zone Aware</h3>
              <p className="text-gray-600">
                Works across time zones. Team members submit when convenient, summaries generated daily.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-gray-600">
              Choose the plan that works for your team size and needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">

            {/* Essentials Plan */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-blue-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>

              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Crown className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Essentials</h3>
                  <p className="text-gray-500">Great for small teams</p>
                </div>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$1.50</span>
                <span className="text-gray-500">/month</span>
                <div className="text-sm text-gray-500 mt-1">
                  Fixed price for up to 4 users
                </div>
                <div className="text-sm text-blue-600 font-medium mt-2">
                  7-day free trial included
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Up to 4 team members</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">AI-powered daily summaries</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Sentiment analysis</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Priority support</span>
                </div>
              </div>

              <a 
                href="/auth/login?plan=essentials"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium text-center block transition-colors"
              >
                Start 7-Day Free Trial
              </a>
            </div>

            {/* Advanced Plan */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Zap className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Advanced</h3>
                  <p className="text-gray-500">For growing teams</p>
                </div>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">$2.00</span>
                <span className="text-gray-500">/user/month</span>
                <div className="text-sm text-purple-600 font-medium mt-2">
                  7-day free trial included
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Unlimited team members</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Advanced analytics</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Custom integrations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Premium support</span>
                </div>
              </div>

              <a 
                href="/auth/login?plan=advanced"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium text-center block transition-colors"
              >
                Start 7-Day Free Trial
              </a>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600">
              Both plans include a 7-day free trial. No credit card required to start.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-indigo-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to transform your standups?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join hundreds of teams already using SyncSimple for better async communication.
          </p>
          <a 
            href="/auth/login"
            className="bg-white hover:bg-gray-100 text-indigo-600 px-8 py-3 rounded-lg font-semibold text-lg transition-colors inline-flex items-center space-x-2"
          >
            <span>Get Started Today</span>
            <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://cfcpcmswpbygvdbyrivt.supabase.co/storage/v1/object/sign/images/syncsimplelogo-color.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9hMThjNGE0NC1hYzkzLTQwYTYtYTk5Yy0wY2NlYTdiZDZmM2UiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvc3luY3NpbXBsZWxvZ28tY29sb3IucG5nIiwiaWF0IjoxNzUxMjE4MjA0LCJleHAiOjE5MDg4OTgyMDR9.qXFyzRLEKJUYFtL5qHQCoCkPHsxjIwQzSWdhHw1Zfms"
                alt="SyncSimple Logo"
                className="h-8 w-auto"
              />
            </div>
            <div className="text-gray-400 text-sm">
              © 2024 SyncSimple. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}